import React from 'react';
import { Button as MuiButton, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import cx from 'classnames';

import Icon from '../../../../../../../../common/general/Icon';

import { ACCEPT_ATTRIBUTE } from './taskAttachmentConstants';
import { AddTaskAttachmentModalComponent, TaskComponent, TaskIconProps } from './taskAttachmentTypes';
import useAddTaskAttachmentModal from './useAddTaskAttachmentModal';

import './AddTaskAttachmentModal.scss';

const TaskIcon = Icon as TaskComponent<TaskIconProps>;

const AddTaskAttachmentModal: AddTaskAttachmentModalComponent = ({ modalBox, taskId, ssuId, onAttachmentSaved }) => {
  const {
    inputRef,
    attachment,
    isDragActive,
    isSaving,
    uploadIsActive,
    saveIsDisabled,
    handleClose,
    openFilePicker,
    handleUploadKeyDown,
    handleFileChange,
    handleDragStart,
    handleDragLeave,
    handleDrop,
    removeAttachment,
    handleSave
  } = useAddTaskAttachmentModal({ modalBox, taskId, ssuId, onAttachmentSaved });

  return (
    <>
      <div className="task-add-attachment-modal-header">
        <h3>Add Attachment</h3>
        <Tooltip title="Close" placement="top">
          <IconButton size="medium" onClick={handleClose} aria-label="Close" className="task-add-attachment-close">
            <TaskIcon>close</TaskIcon>
          </IconButton>
        </Tooltip>
      </div>
      <div className="task-add-attachment-modal-body">
        <input
          ref={inputRef}
          aria-label="Upload attachment"
          className="task-add-attachment-file-input"
          type="file"
          accept={ACCEPT_ATTRIBUTE}
          disabled={!uploadIsActive}
          onChange={handleFileChange}
        />
        <div
          className={cx('task-add-attachment-upload', {
            'task-add-attachment-upload-active': isDragActive,
            'task-add-attachment-upload-disabled': !uploadIsActive
          })}
          role="button"
          tabIndex={uploadIsActive ? 0 : -1}
          aria-disabled={!uploadIsActive}
          onClick={openFilePicker}
          onKeyDown={handleUploadKeyDown}
          onDragEnter={handleDragStart}
          onDragOver={handleDragStart}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="task-add-attachment-upload-inner">
            <TaskIcon suit="material-outline">upload_file</TaskIcon>
            <span>
              <span className="task-add-attachment-upload-action">Click to Upload</span>
              <span className="task-add-attachment-upload-drag-and-drop"> or drag and drop</span>
            </span>
          </div>
        </div>

        <TextField
          className="task-add-attachment-field"
          label="Attachment"
          value={attachment?.name || ''}
          fullWidth
          variant="outlined"
          disabled={!attachment}
          slotProps={{
            htmlInput: {
              readOnly: true,
              'aria-disabled': !attachment
            },
            inputLabel: { shrink: true },
            input: {
              endAdornment: attachment ? (
                <InputAdornment position="end">
                  <Tooltip title="Remove" placement="top">
                    <IconButton
                      size="small"
                      onClick={removeAttachment}
                      aria-label="Remove attachment"
                      disabled={isSaving}
                    >
                      <TaskIcon>close</TaskIcon>
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : null
            }
          }}
        />
      </div>
      <div className="task-add-attachment-modal-footer">
        <MuiButton
          className="task-add-attachment-cancel-button"
          variant="text"
          onClick={handleClose}
          disabled={isSaving}
        >
          Cancel
        </MuiButton>
        <MuiButton
          className="task-add-attachment-save-button"
          variant="contained"
          onClick={handleSave}
          disabled={saveIsDisabled}
        >
          Save
        </MuiButton>
      </div>
    </>
  );
};

AddTaskAttachmentModal.size = 'w350';
AddTaskAttachmentModal.className = 'task-add-attachment-modal';

export default AddTaskAttachmentModal;
